import { theme as antdTheme, type ThemeConfig } from 'antd';
import { AppTheme } from '@shared/services/theme';

const LIGHT_PRIMARY = '#234EEE';
const LIGHT_PRIMARY_HOVER = '#4D79FA';
const LIGHT_PRIMARY_ACTIVE = '#1435C7';

const DARK_CONTAINER_BG = '#2C2E31';
const DARK_PRIMARY_SCALE = {
  1: '#15171B',
  2: '#1D2025',
  3: '#24272C',
  4: '#2C2E31',
  5: '#323437',
  6: '#646669',
  7: '#7B7D80',
  8: '#979A9E',
  9: '#B7B9BE',
  10: '#D1D0C5',
} as const;

export const createTheme = (mode: AppTheme): ThemeConfig => {
  const isDark = mode === AppTheme.dark;
  const colorPrimary = isDark ? DARK_PRIMARY_SCALE[6] : LIGHT_PRIMARY;
  const colorPrimaryHover = LIGHT_PRIMARY_HOVER;
  const colorPrimaryActive = isDark ? DARK_PRIMARY_SCALE[7] : LIGHT_PRIMARY_ACTIVE;
  const colorPrimarySubtle = isDark ? DARK_PRIMARY_SCALE[2] : '#F0F6FF';

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily: 'Inter, sans-serif',
      colorPrimary,
      colorInfo: colorPrimary,
      ...(isDark && {
        colorPrimaryBg: DARK_PRIMARY_SCALE[1],
        colorPrimaryBgHover: DARK_PRIMARY_SCALE[2],
        colorPrimaryBorder: DARK_PRIMARY_SCALE[3],
        colorPrimaryBorderHover: DARK_PRIMARY_SCALE[4],
        colorPrimaryHover: LIGHT_PRIMARY_HOVER,
        colorPrimaryActive: DARK_PRIMARY_SCALE[7],
        colorPrimaryTextHover: LIGHT_PRIMARY_HOVER,
        colorPrimaryText: DARK_PRIMARY_SCALE[9],
        colorPrimaryTextActive: DARK_PRIMARY_SCALE[10],
        colorBgContainer: DARK_CONTAINER_BG,
        colorBgElevated: DARK_CONTAINER_BG,
      }),
    },
    components: {
      Button: {
        colorPrimary,
        colorPrimaryHover,
        colorPrimaryActive,
        colorBorder: 'transparent',
        defaultActiveColor: colorPrimary,
        defaultHoverBg: isDark ? 'rgba(71, 111, 227, 0.16)' : 'rgba(35, 78, 238, 0.1)',
        boxShadow: isDark
          ? '0px 4px 8px 0px rgba(0, 0, 0, 0.3)'
          : '0px 4px 8px 0px rgba(35, 78, 238, 0.1)',
        boxShadowSecondary: isDark
          ? '0px 15px 15px 0px rgba(0, 0, 0, 0.26)'
          : '0px 15px 15px 0px rgba(35, 78, 238, 0.09)',
        boxShadowTertiary: isDark
          ? '0px 34px 20px 0px rgba(0, 0, 0, 0.16)'
          : '0px 34px 20px 0px rgba(35, 78, 238, 0.05)',
        fontSize: 14,
        lineHeight: 22,
        fontWeight: 400,
        controlHeight: 32,
        borderRadius: 6,
        fontSizeLG: 16,
        lineHeightLG: 24,
        controlHeightLG: 40,
        paddingInlineLG: 16,
        borderRadiusLG: 8,
        fontSizeSM: 14,
        lineHeightSM: 22,
        controlHeightSM: 24,
        paddingInlineSM: 8,
        borderRadiusSM: 4,
      },
      Input: {
        borderRadius: 6,
        paddingInline: 8,
        fontSize: 14,
        lineHeight: 22 / 14,
        controlHeight: 32,
        borderRadiusLG: 8,
        paddingInlineLG: 8,
        fontSizeLG: 14,
        lineHeightLG: 22 / 14,
        controlHeightLG: 40,
        hoverBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        activeBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        colorErrorBorder: '#FF4D4F',
      },
      Form: {
        itemMarginBottom: 0,
        fontWeightStrong: 400,
      },
      Checkbox: {
        colorPrimary,
        colorPrimaryHover,
      },
      Spin: {
        colorPrimary,
      },
      Breadcrumb: {
        fontSize: 16,
      },
      Select: {
        borderRadius: 8,
        optionHeight: 40,
        hoverBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        activeBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        optionSelectedBg: colorPrimarySubtle,
      },
      Modal: {
        titleFontSize: 25,
        titleLineHeight: '100%',
        colorBgMask: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
      },
      Tabs: {
        fontSize: 14,
        fontWeightStrong: 400,
        itemActiveColor: colorPrimary,
        itemSelectedColor: colorPrimary,
        itemHoverColor: colorPrimaryHover,
        inkBarColor: colorPrimary,
      },
      Slider: {
        trackBg: colorPrimary,
        trackHoverBg: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        dotActiveBorderColor: colorPrimary,
        handleColor: colorPrimary,
        handleActiveColor: colorPrimaryActive,
        handleActiveOutlineColor: 'rgba(20, 53, 199, 0.2)',
      },
      Radio: {
        buttonSolidCheckedBg: colorPrimary,
        buttonSolidCheckedActiveBg: colorPrimaryActive,
        buttonSolidCheckedHoverBg: isDark ? LIGHT_PRIMARY_HOVER : colorPrimaryHover,
        colorPrimary,
        colorPrimaryActive: colorPrimaryActive,
        colorPrimaryHover: colorPrimaryHover,
        colorPrimaryTextHover: colorPrimaryHover,
      },
      Upload: {
        colorPrimaryHover: colorPrimaryHover,
      },
      DatePicker: {
        activeBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimary,
        colorPrimary,
        hoverBorderColor: isDark ? LIGHT_PRIMARY_HOVER : colorPrimaryHover,
      },
      Card: {
        bodyPadding: 0,
        headerPadding: 0,
      },
      Switch: {
        colorPrimary,
        colorPrimaryHover,
        colorTextQuaternary: '#FFFFFF',
      },
    },
  };
};
